import { useEffect, useMemo } from 'react';

import { Flex, Separator, useBreakpointValue } from '@chakra-ui/react';

import isNil from 'lodash/isNil';

import { dateHelpers } from '@nocowanie/core';
import {
    GuestsPicker,
    GuestsPickerInputDataModel,
    RangePicker,
    RangePickerDates,
} from '@nocowanie/common-ui';

import { environment } from '@app/environments/environment';
import { accommodationHelpers } from '@app/helpers';
import { QueryAccommodationArgs } from '@app/models/api/api-types';
import { accommodationSlice, commonUiStateSlice, useAppDispatch, useAppSelector } from '@app/state';
import { fetchCalendarAvailability } from '@app/state/slices/accommodation.slice';

import { AccommodationConfigurationFormProps } from './accommodation-configuration-form.props';

export const AccommodationConfigurationForm = ({
    withDynamicLayout = false,
    withError = true,
    ...props
}: AccommodationConfigurationFormProps) => {
    const dispatch = useAppDispatch();
    const filters = useAppSelector(accommodationSlice.selectors.selectFilters);
    const { checkIn, checkOut } = filters ?? {};
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const isCalendarAvailabilityLoading = useAppSelector(
        accommodationSlice.selectors.selectIsCalendarAvailabilityLoading,
    );
    const calendarAvailabilityErrors = useAppSelector(
        accommodationSlice.selectors.selectCalendarAvailabilityErrors,
    );
    const isDatesAvailabilityError = useAppSelector(
        accommodationSlice.selectors.selectIsDatesAvailabilityError,
    );
    const rangePickerDayConfig = useAppSelector(
        accommodationSlice.selectors.selectRangePickerDayConfig,
    );
    const rangePickerExcludedDays = useAppSelector(
        accommodationSlice.selectors.selectRangePickerExcludedDays,
    );
    const selectedDates = useMemo(() => {
        return checkIn && checkOut ? accommodationHelpers.getDates(checkIn, checkOut) : undefined;
    }, [checkOut, checkIn]);

    const guestsPickerInputData = useMemo(() => {
        return {
            adultsCount: filters?.guestsCount,
            childrenCount: filters?.childrenCount,
            childrenAges: filters?.childrenAges,
        };
    }, [filters?.guestsCount, filters?.childrenCount, filters?.childrenAges]);

    const isHorizontal = useBreakpointValue({
        base: withDynamicLayout,
        xl: undefined,
    });

    useEffect(() => {
        if (
            isCalendarAvailabilityLoading ||
            !isNil(rangePickerDayConfig) ||
            !isNil(calendarAvailabilityErrors)
        ) {
            return;
        }

        dispatch(
            fetchCalendarAvailability({
                accommodationId: undefined,
            }),
        );
    }, [calendarAvailabilityErrors, isCalendarAvailabilityLoading, rangePickerDayConfig]);

    return (
        <form noValidate>
            <Flex
                borderWidth={'1px'}
                borderStyle={'solid'}
                borderColor={'colors.border'}
                borderRadius={'md'}
                maxW={{
                    md: '360px',
                }}
                {...props}
            >
                <RangePicker
                    isClearable
                    selectedDates={selectedDates}
                    onRangeChange={(dates: RangePickerDates) => {
                        dispatch(
                            accommodationSlice.actions.updateFilters({
                                checkIn: dates[0]
                                    ? dateHelpers.format(dates[0], environment.API_DATE_FORMAT)
                                    : null,
                                checkOut: dates[1]
                                    ? dateHelpers.format(dates[1], environment.API_DATE_FORMAT)
                                    : null,
                            }),
                        );
                    }}
                    onError={errorMessage => {
                        if (withError && errorMessage) {
                            dispatch(
                                accommodationSlice.actions.setCalendarAvailabilityErrorText(
                                    errorMessage,
                                ),
                            );
                        }
                    }}
                    inputProps={{
                        invalid: isDatesAvailabilityError || !selectedDates,
                        inputProps: {
                            border: 'none',
                            cursor: 'pointer',
                        },
                        flex: 2,
                    }}
                    isMobileBrowser={isMobileBrowser}
                    daysConfig={rangePickerDayConfig}
                    excludeDateStrings={rangePickerExcludedDays}
                    pickerProps={{
                        popperClassName: !isMobileBrowser
                            ? 'react-datepicker-popper--full'
                            : undefined,
                    }}
                />
                <Separator
                    orientation={isHorizontal ? undefined : 'vertical'}
                    borderColor={'inherit'}
                    h={'auto'}
                    w={'auto'}
                    alignSelf={'stretch'}
                    my={isHorizontal ? 0 : 2}
                    mx={isHorizontal ? 2 : 0}
                />
                <GuestsPicker
                    inputData={guestsPickerInputData as GuestsPickerInputDataModel}
                    noArrow={true}
                    onInputDataChanged={data => {
                        dispatch(
                            accommodationSlice.actions.updateFilters({
                                guestsCount: data.adultsCount,
                                childrenCount: data.childrenCount,
                                childrenAges:
                                    data.childrenAges as QueryAccommodationArgs['childrenAges'],
                            }),
                        );
                    }}
                    triggerProps={{
                        flex: 1,
                        border: 0,
                        bg: 'white',
                        justifyContent: withDynamicLayout
                            ? { md: 'flex-start', xl: 'center' }
                            : undefined,
                        padding: withDynamicLayout ? { md: '0.75rem', xl: undefined } : undefined,
                    }}
                    isMobileBrowser={isMobileBrowser}
                />
            </Flex>
        </form>
    );
};
